import { Columns03, CursorBox, Edit01, FilterLines, Package, Plus, SearchLg, Trash01, XClose } from '@untitled-ui/icons-react/build/cjs';
import { debounce, isEqual } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setTeamMemberOptions } from 'src/app/MyQuote/quotingDashboard.helpers';
import BulkActionBar from 'src/components/BulkActionBar';
import ButtonCmp from 'src/components/ButtonCmp';
import CheckBox from 'src/components/CheckBox';
import CustomPagination from 'src/components/CustomPagination';
import DateRangePicker from 'src/components/DateRangePicker';
import DeleteModal from 'src/components/DeleteModal';
import DraggableLayout from 'src/components/DraggableLayout';
import InputText from 'src/components/InputText/InputText';
import NotFoundUI from 'src/components/NotFoundUI';
import PageSectionLayout from 'src/components/PageSectionLayout';
import Header from 'src/components/PageSectionLayout/Header/Header';
import SelectBox from 'src/components/SelectBox/SelectBox';
import TableCmp from 'src/components/TableCmp';
import FilterLayout from 'src/components/TableFilter/FilterLayout';
import TableTabButton from 'src/components/TableTabButton';
import CreateViewModal from 'src/components/TableTabButton/CreateViewModal';
import TooltipCmp from 'src/components/TooltipCmp';
import { useAddressDetails } from 'src/hooks/useAddressDetails';
import { setOrderColumns } from 'src/redux/basic.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { orderList, orderListCount, getOrderViewList, createOrderView, updateOrderView, deleteOrderView, updateOrderTableColumnSettings } from 'src/services/OrderService';
import { createDateWithoutTimezoneConversion } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

import OrderDraggableCell from './Component/OrderDraggableCell';
import NewOrderRow from './NewOrderRow';
import {
    CP_ORDER_TABLE_ALIAS,
    MAX_ORDER_VIEW_COUNT,
    DEFAULT_ORDER_TAB_VALUES,
    SORTABLE_INITIAL_STATE,
    BULK_SELECTION_INITIAL_STATE,
    ORDER_STATUS,
    ORDER_STATUS_OPTIONS,
    ORDER_CLASSIFICATION_OPTIONS,
} from './order.const';
import { transformColumnsToHeadCells, formateTabData, extractViewNames, formatRowData, isOrderParamsChanged, IOrderTab } from './order.helpers';
import {
    setOrderParams,
    setActiveTab as setActiveTabRedux,
    setTabsData as setTabsDataRedux,
    setIsFetched,
    orderParamsData,
    activeTabData,
    tabsDataData,
    isFetchedTabsData,
} from './OrderDashboard.slice';
import NewOrderLoaderRow from './skeletons/NewOrderLoaderRow';

const recordsPerPageArray = [
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
];

const initParams: any = {
    search: '',
    sortType: 'desc',
    sortField: 'updatedAt',
    page: 1,
    limit: 50,
    status: ORDER_STATUS.ALL,
    classification: null,
    member: 'all',
    startDate: null,
    endDate: null,
    shipper: null,
    consignee: null,
};

const Orders = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const uiState = useSelector((state: any) => state.MobileView);
    const { isTeamLoading, allTeamMemberListOptions } = useSelector((state: any) => state.basic);
    const { shipperOptions = [], consigneeOptions = [], handleSearchShipper, handleSearchConsignee } = useAddressDetails();

    // Redux selectors — state persists across route changes
    const reduxOrderParams = useAppSelector(orderParamsData);
    const reduxActiveTab = useAppSelector(activeTabData);
    const reduxTabsData = useAppSelector(tabsDataData);
    const isFetchedTabs = useAppSelector(isFetchedTabsData);

    // AbortController refs for canceling in-flight requests
    const abortControllerRef = useRef<AbortController | null>(null);
    const countAbortControllerRef = useRef<AbortController | null>(null);

    // Tab state — initialized from Redux
    const [tabsData, setTabsData] = useState<IOrderTab[]>(reduxTabsData.length > 0 ? reduxTabsData : []);
    const [activeTab, setActiveTab] = useState<string>(reduxActiveTab);
    const [isTabsLoading, setIsTabsLoading] = useState(false);
    const [orderCount, setOrderCount] = useState<any[]>([]);

    // UI state
    const [isShowAction, setIsShowAction] = useState(false);
    const [isFilterView, setIsFilterView] = useState<any>({ isShow: false, data: null });

    // Date range state — initialized from Redux params
    const [selectedTimeRange, setSelectedTimeRange] = useState<string>('custom');
    const [isPrevYearDisable, setIsPrevYearDisable] = useState<any>(false);
    const [startDate, setStartDate] = useState<any>(reduxOrderParams?.startDate ? moment(reduxOrderParams.startDate).toDate() : null);
    const [endDate, setEndDate] = useState<any>(reduxOrderParams?.endDate ? moment(reduxOrderParams.endDate).toDate() : null);
    const [allTimeStartDate] = useState<any>('');

    // Params & data state — initialized from Redux
    const [params, setParams] = useState(reduxOrderParams);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState(reduxOrderParams?.search || '');
    const [ordersList, setOrdersList] = useState<any>([]);
    const [totals, setTotals] = useState(0);
    const [filterCount, setFilterCount] = useState<number>(0);
    const [searchApplied, setSearchApplied] = useState(false);
    const [teamMemberListOptions, setTeamMemberListOptions] = useState<any[]>([]);
    const [selectedUserOption, setSelectedUserOption] = useState<any>(null);
    const [searchTeamMember, setSearchTeamMember] = useState<string>('');

    // Custom views state
    const initAction = { create_view: false, delete_view: false, rename_view: false, data: null as any };
    const [action, setAction] = useState<any>(initAction);
    const [viewNames, setViewNames] = useState<string[]>([]);
    const [isViewChangeLoading, setIsViewChangeLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    // Bulk row selection state
    const [bulkSelection, setBulkSelection] = useState(BULK_SELECTION_INITIAL_STATE);
    const selectedOrderIds = bulkSelection.data;
    const [showAllSelected, setShowAllSelected] = useState(false);

    const setSelectedOrderIds = useCallback((ids: number[]) => {
        if (ids.length === 0) setShowAllSelected(false);
        setBulkSelection({ isOpen: ids.length > 0, data: ids });
    }, []);

    const handleToggleSelectOrder = useCallback((id: number) => {
        setBulkSelection(({ data }) => {
            const next = data.includes(id) ? data.filter((i) => i !== id) : [...data, id];
            if (next.length === 0) setShowAllSelected(false);

            return { isOpen: next.length > 0, data: next };
        });
    }, []);

    const displayedOrders = useMemo(() => (showAllSelected ? ordersList.filter((o: any) => selectedOrderIds.includes(o.id)) : ordersList), [showAllSelected, ordersList, selectedOrderIds]);

    // Column rearrangement state
    const [isRowSortable, setIsRowSortable] = useState<{ isOpen: boolean; data: any[] }>(SORTABLE_INITIAL_STATE);
    const [columnsOrder, setColumnsOrder] = useState<any[]>([]);
    const orderColumns = useAppSelector((state: any) => state.basic.orderColumns);

    const isSaveEnable = useMemo(
        () => isOrderParamsChanged(isFilterView?.data, params),
        [
            params?.status,
            params?.classification,
            params?.search,
            params?.sortType,
            params?.sortField,
            params?.member,
            params?.startDate,
            params?.endDate,
            params?.shipper,
            params?.consignee,
            isFilterView?.data,
        ]
    );

    const isCreateViewEnable = useMemo(() => tabsData?.length < MAX_ORDER_VIEW_COUNT, [tabsData?.length]);

    // Initialize default tabs — only when tabs haven't been fetched yet and local state is empty
    useEffect(() => {
        if (!isFetchedTabs && tabsData.length === 0) {
            setIsTabsLoading(true);
            const defaultTabs: IOrderTab[] = [
                { value: ORDER_STATUS.ALL, name: 'All', count: 0 },
                { value: ORDER_STATUS.BOOKED, name: 'Booked', count: 0 },
                { value: ORDER_STATUS.DISPATCHED, name: 'Dispatched', count: 0 },
                { value: ORDER_STATUS.IN_TRANSIT, name: 'In Transit', count: 0 },
                { value: ORDER_STATUS.DELIVERED, name: 'Delivered', count: 0 },
                { value: ORDER_STATUS.COMPLETED, name: 'Completed', count: 0 },
            ];

            setTabsData(defaultTabs);
            dispatch(setTabsDataRedux(defaultTabs));
            setIsTabsLoading(false);
        }
    }, [isFetchedTabs, tabsData.length]);

    // Fetch custom saved views and merge with default tabs
    const getOrderViewListData = async () => {
        setIsTabsLoading(true);
        getOrderViewList({ tableAlias: CP_ORDER_TABLE_ALIAS })
            .then((response: any) => {
                dispatch(setIsFetched(true));
                const responseData = response?.data;

                if (responseData?.length > 0) {
                    const tabData = formateTabData(responseData);
                    const currentTabs = tabsData.length > 0 ? tabsData : [];
                    const mergedTabs = Array.from(new Map([...currentTabs, ...tabData].map((tab: IOrderTab) => [tab.value, tab])).values());

                    setTabsData(mergedTabs);
                    dispatch(setTabsDataRedux(mergedTabs));
                    setViewNames(extractViewNames(mergedTabs));
                }

                // If active tab is a custom view, sync params from latest DB data
                if (!DEFAULT_ORDER_TAB_VALUES.includes(activeTab)) {
                    const activeView = responseData?.find((view: any) => view.id?.toString() === activeTab);

                    if (activeView) {
                        const updatedParams = {
                            ...params,
                            page: 1,
                            search: activeView.appliedFilters?.search ?? '',
                            member: activeView.appliedFilters?.member ?? 'all',
                            startDate: activeView.appliedFilters?.startDate ?? null,
                            endDate: activeView.appliedFilters?.endDate ?? null,
                            sortType: activeView.appliedSorting?.sortType ?? params.sortType,
                            sortField: activeView.appliedSorting?.sortField ?? params.sortField,
                            status: activeView.appliedFilters?.status ?? ORDER_STATUS.ALL,
                            classification: activeView.appliedFilters?.classification ?? null,
                            shipper: activeView.appliedFilters?.shipper ?? null,
                            consignee: activeView.appliedFilters?.consignee ?? null,
                        };
                        setParams(updatedParams);
                        dispatch(setOrderParams(updatedParams));
                        setSearch(activeView.appliedFilters?.search ?? '');
                        setStartDate(activeView.appliedFilters?.startDate ? moment(activeView.appliedFilters.startDate).toDate() : null);
                        setEndDate(activeView.appliedFilters?.endDate ? moment(activeView.appliedFilters.endDate).toDate() : null);
                    } else {
                        // Active custom view no longer exists — fall back to 'all'
                        setActiveTab(ORDER_STATUS.ALL);
                        dispatch(setActiveTabRedux(ORDER_STATUS.ALL));
                        setSearch('');
                        setStartDate(null);
                        setEndDate(null);
                        setParams({ ...initParams });
                        dispatch(setOrderParams({ ...initParams }));
                    }
                }
            })
            .catch((error: any) => {
                console.log('getOrderViewListData error', error);
            })
            .finally(() => {
                setIsTabsLoading(false);
            });
    };

    // Fetch custom tabs only after default tabs are initialized
    useEffect(() => {
        if (!isFetchedTabs && tabsData.length > 0) {
            getOrderViewListData();
        }
    }, [tabsData.length, isFetchedTabs]);

    useEffect(() => {
        if (orderCount.length > 0 && tabsData.length > 0) {
            const updatedTabs = tabsData.map((tab) => {
                if (tab.value === ORDER_STATUS.ALL) return tab;
                const newCount = orderCount.find((item: any) => item.value === tab.value);

                return newCount ? { ...tab, count: newCount.count } : tab;
            });

            const hasChanges = updatedTabs.some((tab, index) => tab.count !== tabsData[index]?.count);

            if (hasChanges) {
                setTabsData(updatedTabs);
                dispatch(setTabsDataRedux(updatedTabs));
            }
        }
    }, [orderCount, tabsData.length]);

    // Fetch order counts for tab badges
    const getOrderCount = async () => {
        countAbortControllerRef.current?.abort();
        countAbortControllerRef.current = new AbortController();
        const signal = countAbortControllerRef.current.signal;

        try {
            // Use initParams (not Redux-persisted params) so tab counts always reflect
            // total unfiltered counts per status — user filters should not affect badge numbers
            const response = (await orderListCount({ ...initParams, status: ORDER_STATUS.ALL }, signal)) as any;
            setOrderCount(response?.counts ?? []);
        } catch (error: any) {
            if (error.code === 'ERR_CANCELED') return;
        }
    };

    useEffect(() => {
        getOrderCount();

        return () => {
            countAbortControllerRef.current?.abort();
        };
    }, []);

    // Team member list options come from /common/init (see init.thunks INIT_SECTIONS 'members')
    // and are read straight off redux below — this page no longer fetches them for itself.

    useEffect(() => {
        if (!isTeamLoading) {
            setTeamMemberListOptions(setTeamMemberOptions('all', params?.member, allTeamMemberListOptions));
        }
    }, [isTeamLoading]);

    useEffect(() => {
        if (allTeamMemberListOptions && allTeamMemberListOptions.length > 0) {
            setTeamMemberListOptions(setTeamMemberOptions('all', params?.member, allTeamMemberListOptions));
        }
    }, [allTeamMemberListOptions]);

    // Update selected user option when member param changes, and re-hydrate list when cleared
    useEffect(() => {
        if (params?.member === 'all') {
            setTeamMemberListOptions(setTeamMemberOptions('all', params?.member, allTeamMemberListOptions));
        }

        if (params?.member && params?.member !== 'all') {
            const selectedMember = teamMemberListOptions.filter((user: any) => params?.member === user.value);
            setSelectedUserOption(selectedMember);
        } else {
            setSelectedUserOption(null);
        }
    }, [params?.member, allTeamMemberListOptions]);

    // Filter team member dropdown by typed search with 250ms debounce
    useEffect(() => {
        if (searchTeamMember) {
            const delayDebounceFn = setTimeout(() => {
                setTeamMemberListOptions(
                    setTeamMemberOptions(
                        'all',
                        params?.member,
                        allTeamMemberListOptions.filter((member: any) => `${member.firstName} ${member.lastName}`.toLowerCase().includes(searchTeamMember.toLowerCase()))
                    )
                );
            }, 250);

            return () => clearTimeout(delayDebounceFn);
        } else {
            setTeamMemberListOptions(setTeamMemberOptions('all', params?.member, allTeamMemberListOptions));
        }
    }, [searchTeamMember]);

    // Initialize isPrevYearDisable based on loaded dates
    useEffect(() => {
        if (startDate && endDate) {
            const twoYearsAgo = moment().subtract(1, 'years').startOf('year');
            const daysDiff = moment(endDate).diff(moment(startDate), 'days') + 1;
            const prevMonth = moment(startDate).subtract(daysDiff, 'day');

            setIsPrevYearDisable(prevMonth.isSameOrBefore(twoYearsAgo, 'days'));
        }
    }, []);

    const hasPriorityOrder = useMemo(() => ordersList.some((o: any) => o.isPriority), [ordersList]);

    // Dynamic head cells using transformColumnsToHeadCells helper
    const headCells = useMemo(() => {
        const hasData = ordersList.length > 0 && !isLoading;
        const result = transformColumnsToHeadCells(orderColumns, hasData, t);

        const checkboxCell = {
            id: 'checkbox',
            name: (
                <CheckBox
                    id="selectAllOrders"
                    checked={false}
                    onChangeFunc={() => setSelectedOrderIds(ordersList.map((o: any) => o.id))}
                    name="selectAllOrders"
                    value="selectAllOrders"
                    disabled={isLoading}
                />
            ),
            sortable: false,
            visible: true,
            rowClassName: '!pr-2 sticky left-0 bg-inherit',
        };

        const actionCell = {
            id: 'action',
            name: '',
            sortable: false,
            // Keep the column during loading (list is cleared on fetch, so hasPriorityOrder is always
            // false then) — otherwise the skeleton omits it and the column pops in when data arrives.
            visible: hasPriorityOrder || isLoading,
            rowClassName: 'sticky right-0 bg-inherit',
        };

        return [checkboxCell, ...result, actionCell];
    }, [ordersList, isLoading, t, orderColumns, setSelectedOrderIds, hasPriorityOrder]);

    const fetchOrdersData = async () => {
        setIsLoading(true);
        setOrdersList([]);

        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();

        try {
            const response = (await orderList(params, abortControllerRef.current.signal)) as any;

            if (response?.data) {
                setOrdersList(response?.data);
            }

            const totalRecords = response?.pagination?.totalRecords || response?.pagination?.total || response?.total || response?.totalRecords || response?.data?.length || 0;
            setTotals(totalRecords);

            setIsLoading(false);
        } catch (e: any) {
            if (e.code === 'ERR_CANCELED') return;
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchOrdersData();

        return () => {
            abortControllerRef.current?.abort();
        };
    }, [params]);

    // Sync activeTab with params.status only when on a default tab
    useEffect(() => {
        if (params.status && DEFAULT_ORDER_TAB_VALUES.includes(params.status) && DEFAULT_ORDER_TAB_VALUES.includes(activeTab) && params.status !== activeTab) {
            setActiveTab(params.status);
            dispatch(setActiveTabRedux(params.status));
        }
    }, [params.status]);

    // Persist params to Redux when they change
    useEffect(() => {
        dispatch(setOrderParams(params));
    }, [params]);

    useEffect(() => {
        let fCount = 0;
        if (params?.startDate && params?.endDate) fCount += 1;
        if (params.member !== 'all' && params.member) fCount += 1;
        if (params.classification && params.classification !== 'all') fCount += 1;
        if (params.shipper) fCount += 1;
        if (params.consignee) fCount += 1;

        if (uiState?.isMobile || uiState?.currentBreakpoint === 'SM') {
            if (params.status !== ORDER_STATUS.ALL) fCount += 1;
        }

        setFilterCount(fCount);
    }, [params, uiState]);

    useEffect(() => {
        if (!isEqual(initParams, params)) {
            setSearchApplied(true);
        } else {
            setSearchApplied(false);
        }
    }, [params]);

    const handleDatePickerChange = (date: any, type: any) => {
        const [start, end] = date;
        setSelectedTimeRange(type);

        const twoYearsAgo = moment().subtract(1, 'years').startOf('year');
        const daysDiff = moment(end).diff(moment(start), 'days') + 1;
        const prevMonth = moment(start).subtract(daysDiff, 'day');

        setIsPrevYearDisable(prevMonth.isSameOrBefore(twoYearsAgo, 'days'));

        const finalStartDate = start ?? null;
        const finalEndDate = end ? end ?? null : start ?? null;

        setStartDate(finalStartDate);
        setEndDate(finalEndDate);

        setParams((old: any) => ({
            ...old,
            page: 1,
            startDate: finalStartDate ? moment(finalStartDate).format('YYYY-MM-DD') : null,
            endDate: finalEndDate ? moment(finalEndDate).format('YYYY-MM-DD') : null,
        }));
    };

    const getFilterRange = (type?: string) => {
        var startDT = new Date();
        var endDT = new Date();
        const currentYear = new Date().getFullYear();
        const twoYearsAgo = moment().subtract(1, 'years').startOf('year');

        if (type === 'prev' && !isPrevYearDisable) {
            if (selectedTimeRange === 'yearly') {
                startDT = moment(startDate).subtract(1, 'year').toDate();
                endDT = moment(endDate).subtract(1, 'year').toDate();
                const prevMonth = moment(moment(startDate, 'YYYY')).subtract(1, 'year').format('YYYY');

                if (currentYear - parseInt(prevMonth) >= 1) {
                    setIsPrevYearDisable(true);
                } else {
                    setIsPrevYearDisable(false);
                }
            } else if (selectedTimeRange === 'weekly') {
                startDT = moment(endDate).subtract(1, 'week').isoWeekday(0).toDate();
                endDT = moment(endDate).subtract(1, 'week').isoWeekday(6).toDate();
                const prevMonth = moment(startDate).subtract(1, 'week');

                if (prevMonth.isSameOrBefore(twoYearsAgo, 'week')) {
                    setIsPrevYearDisable(true);
                } else {
                    setIsPrevYearDisable(false);
                }
            } else if (selectedTimeRange === 'monthly') {
                startDT = moment(startDate).subtract(1, 'months').startOf('month').toDate();
                endDT = moment(startDate).subtract(1, 'months').endOf('month').toDate();
                const prevMonth = moment(startDate).subtract(1, 'months');

                if (prevMonth.isSameOrBefore(twoYearsAgo, 'month')) {
                    setIsPrevYearDisable(true);
                } else {
                    setIsPrevYearDisable(false);
                }
            } else if (selectedTimeRange === 'daily') {
                startDT = moment(startDate).subtract(1, 'days').toDate();
                endDT = moment(endDate).subtract(1, 'days').toDate();
                const prevMonth = moment(startDate).subtract(1, 'day');

                if (prevMonth.isSameOrBefore(twoYearsAgo, 'day')) {
                    setIsPrevYearDisable(true);
                } else {
                    setIsPrevYearDisable(false);
                }
            } else {
                if (selectedTimeRange === 'quarterly') {
                    startDT = moment(moment(startDate).subtract(1, 'quarters').startOf('quarter'), 'YYYY-MM-DD').toDate();
                    endDT = moment(moment(startDate).subtract(1, 'quarters').endOf('quarter'), 'YYYY-MM-DD').toDate();
                    const prevMonth = moment(startDate).subtract(1, 'quarters');

                    if (prevMonth.isSameOrBefore(twoYearsAgo, 'quarters')) {
                        setIsPrevYearDisable(true);
                    } else {
                        setIsPrevYearDisable(false);
                    }
                } else if (selectedTimeRange === 'last_year') {
                    startDT = moment(startDate).subtract(1, 'year').toDate();
                    endDT = moment(startDate).subtract(1, 'year').toDate();
                } else {
                    const daysDiff = moment(endDate).diff(moment(startDate), 'days') + 1;
                    startDT = moment(startDate).clone().subtract(daysDiff, 'days').toDate();
                    endDT = moment(endDate).clone().subtract(daysDiff, 'days').toDate();
                    const daysDiffN = moment(endDT).diff(moment(startDT), 'days') + 1;
                    const prevMonth = moment(startDT).subtract(daysDiffN, 'day');

                    if (prevMonth.isSameOrBefore(twoYearsAgo, 'days')) {
                        setIsPrevYearDisable(true);
                    } else {
                        setIsPrevYearDisable(false);
                    }
                }
            }
            setStartDate(startDT);
            setEndDate(endDT);
            setParams((old: any) => ({
                ...old,
                page: 1,
                startDate: moment(startDT).format('YYYY-MM-DD'),
                endDate: moment(endDT).format('YYYY-MM-DD'),
            }));
        } else if (type === 'next') {
            setIsPrevYearDisable(false);

            if (selectedTimeRange === 'yearly') {
                startDT = moment(startDate).add(1, 'year').toDate();
                endDT = moment(endDate).add(1, 'year').toDate();
            } else if (selectedTimeRange === 'weekly') {
                startDT = moment(endDate).add(1, 'week').isoWeekday(0).toDate();
                endDT = moment(endDate).add(1, 'week').isoWeekday(6).toDate();
            } else if (selectedTimeRange === 'monthly') {
                startDT = moment(startDate).add(1, 'months').startOf('month').toDate();
                endDT = moment(endDate).add(1, 'months').endOf('month').toDate();
            } else if (selectedTimeRange === 'daily') {
                startDT = moment(startDate).add(1, 'days').toDate();
                endDT = moment(endDate).add(1, 'days').toDate();
            } else {
                if (selectedTimeRange === 'quarterly') {
                    startDT = moment(moment(startDate).add(1, 'quarters').startOf('quarter'), 'YYYY-MM-DD').toDate();
                    endDT = moment(moment(startDate).add(1, 'quarters').endOf('quarter'), 'YYYY-MM-DD').toDate();
                } else if (selectedTimeRange === 'last_year') {
                    startDT = moment(startDate).add(1, 'year').toDate();
                    endDT = moment(startDate).add(1, 'year').toDate();
                } else {
                    const daysDiff = moment(endDate).diff(moment(startDate), 'days') + 1;
                    startDT = moment(startDate).clone().add(daysDiff, 'days').toDate();
                    endDT = moment(endDate).clone().add(daysDiff, 'days').toDate();
                }
            }
            setStartDate(startDT);
            setEndDate(endDT);
            setParams((old: any) => ({
                ...old,
                page: 1,
                startDate: moment(startDT).format('YYYY-MM-DD'),
                endDate: moment(endDT).format('YYYY-MM-DD'),
            }));
        }
    };

    const searchDebounce = useCallback(
        debounce((debSearch: string) => setParams((old: any) => ({ ...old, search: debSearch, page: 1 })), 300),
        []
    );

    const handleFilter = (event: any) => {
        const { value } = event.target;

        const trimmedValue = value.trim().replace(/\s+/g, ' ');

        setSearch(value);

        if (trimmedValue || value === '') {
            searchDebounce(trimmedValue);
        }
    };

    const onClearFilter = () => {
        if (uiState?.isMobile || uiState?.currentBreakpoint === 'SM') {
            setParams(initParams);
            setSearch('');
            setStartDate(null);
            setEndDate(null);
            setSelectedTimeRange('custom');
        } else {
            setParams((old: any) => ({
                ...old,
                page: 1,
                startDate: null,
                endDate: null,
                member: 'all',
                classification: null,
                shipper: null,
                consignee: null,
            }));
            setStartDate(null);
            setEndDate(null);
            setSelectedTimeRange('custom');
        }

        setFilterCount(0);
    };

    const handlePagination = (page: number) => {
        setParams({ ...params, page });
    };

    const onRowClick = (order: any) => () => {
        const currentTabData = tabsData.find((tab) => tab.value === activeTab);

        if (currentTabData?.appliedSorting) {
            dispatch(
                setOrderParams({
                    ...initParams,
                    search: currentTabData?.appliedFilters?.search ?? '',
                    member: currentTabData?.appliedFilters?.member ?? 'all',
                    startDate: currentTabData?.appliedFilters?.startDate ?? null,
                    endDate: currentTabData?.appliedFilters?.endDate ?? null,
                    sortType: currentTabData?.appliedSorting?.sortType ?? initParams.sortType,
                    sortField: currentTabData?.appliedSorting?.sortField ?? initParams.sortField,
                    status: currentTabData?.appliedFilters?.status ?? 'All',
                    classification: currentTabData?.appliedFilters?.classification ?? null,
                    shipper: currentTabData?.appliedFilters?.shipper ?? null,
                    consignee: currentTabData?.appliedFilters?.consignee ?? null,
                })
            );
        } else {
            dispatch(setOrderParams({ ...initParams, status: activeTab }));
        }
        navigate(`/orders/${order?.id}`, {
            state: { sourcePage: 'OrdersDashboard', ...order },
        });
    };

    const handleOutSideClick = () => {
        setIsShowAction(false);
    };

    // Custom views CRUD handlers
    const handleAction =
        (key: string, data: any = null) =>
        () => {
            setAction((old: any) => ({ ...old, [key]: true, data }));
        };

    const handleClose = useCallback(() => {
        setAction({ create_view: false, delete_view: false, rename_view: false, data: null });
    }, []);

    const handleUpdateView = async (data: any) => {
        setIsViewChangeLoading(true);
        updateOrderView(data?.id, data)
            .then((response: any) => {
                if (response?.data) {
                    const responseData = response.data;
                    const tabIndex = tabsData.findIndex((tab: any) => tab.id === responseData?.id);

                    if (tabIndex !== -1) {
                        const updatedTabs = [...tabsData];
                        updatedTabs[tabIndex] = {
                            appliedFilters: responseData?.appliedFilters,
                            appliedSorting: responseData?.appliedSorting,
                            name: responseData?.name,
                            value: responseData?.id?.toString(),
                            count: response?.count ?? updatedTabs[tabIndex]?.count,
                            id: responseData?.id,
                            isIconShow: true,
                        };

                        setTabsData(updatedTabs);
                        dispatch(setTabsDataRedux(updatedTabs));
                        setViewNames(extractViewNames(updatedTabs));

                        // Skip params update on rename — only the tab name changed, not the filters.
                        // Updating params would trigger fetchOrdersData unnecessarily.
                        if (!data?.rename) {
                            const updatedParams = {
                                ...params,
                                page: 1,
                                search: responseData?.appliedFilters?.search,
                                member: responseData?.appliedFilters?.member,
                                startDate: responseData?.appliedFilters?.startDate,
                                endDate: responseData?.appliedFilters?.endDate,
                                sortType: responseData?.appliedSorting?.sortType,
                                sortField: responseData?.appliedSorting?.sortField,
                                status: responseData?.appliedFilters?.status,
                                classification: responseData?.appliedFilters?.classification,
                                shipper: responseData?.appliedFilters?.shipper,
                                consignee: responseData?.appliedFilters?.consignee,
                            };
                            setParams(updatedParams);
                            dispatch(setOrderParams(updatedParams));
                        }
                    }

                    if (!data?.rename) {
                        setIsFilterView({ isShow: false, data: null });
                    }
                    setIsShowAction(false);
                    handleClose();
                }
            })
            .catch(async (error: any) => {
                if (error?.response?.status === 422) {
                    setIsError(true);
                }
            })
            .finally(() => {
                setIsViewChangeLoading(false);
            });
    };

    const handleSave = (data: any) => {
        if (data?.rename) {
            handleUpdateView({ ...data, tableAlias: CP_ORDER_TABLE_ALIAS });
        } else {
            setIsViewChangeLoading(true);
            const payload = {
                name: data.name,
                tableAlias: CP_ORDER_TABLE_ALIAS,
                appliedFilters: {
                    search: params?.search || '',
                    member: params?.member || 'all',
                    startDate: params?.startDate || '',
                    endDate: params?.endDate || '',
                    status: params?.status || ORDER_STATUS.ALL,
                    classification: params?.classification || null,
                    shipper: params?.shipper || null,
                    consignee: params?.consignee || null,
                },
                appliedSorting: {
                    sortType: params?.sortType || 'desc',
                    sortField: params?.sortField || 'createdAt',
                },
            };
            createOrderView(payload)
                .then((response: any) => {
                    if (response?.data) {
                        const responseData = response.data;
                        const tabData: IOrderTab = {
                            appliedFilters: responseData?.appliedFilters,
                            appliedSorting: responseData?.appliedSorting,
                            name: responseData?.name,
                            value: responseData?.id?.toString(),
                            count: response?.count ?? 0,
                            id: responseData?.id,
                            isIconShow: true,
                        };
                        const newTabs = [...tabsData, tabData];
                        setTabsData(newTabs);
                        dispatch(setTabsDataRedux(newTabs));
                        setViewNames((prev: any) => [...prev, responseData?.name]);

                        setActiveTab(tabData?.value);
                        dispatch(setActiveTabRedux(tabData?.value));

                        const updatedParams = {
                            ...params,
                            page: 1,
                            search: responseData?.appliedFilters?.search,
                            member: responseData?.appliedFilters?.member,
                            startDate: responseData?.appliedFilters?.startDate,
                            endDate: responseData?.appliedFilters?.endDate,
                            sortType: responseData?.appliedSorting?.sortType,
                            sortField: responseData?.appliedSorting?.sortField,
                            status: responseData?.appliedFilters?.status,
                            classification: responseData?.appliedFilters?.classification,
                            shipper: responseData?.appliedFilters?.shipper,
                            consignee: responseData?.appliedFilters?.consignee,
                        };
                        setParams(updatedParams);
                        dispatch(setOrderParams(updatedParams));

                        setIsFilterView({ isShow: false, data: null });
                        setIsShowAction(false);
                        handleClose();
                    }
                })
                .catch((error: any) => {
                    if (error?.response?.status === 422) {
                        setIsError(true);
                    }
                })
                .finally(() => {
                    setIsViewChangeLoading(false);
                });
        }
    };

    const handleViewSave = () => {
        if (isFilterView.data?.isIconShow) {
            // Editing existing custom view — save directly
            const payload = {
                id: isFilterView?.data?.id,
                name: isFilterView?.data?.name || '',
                tableAlias: CP_ORDER_TABLE_ALIAS,
                appliedFilters: {
                    search: params?.search || '',
                    member: params?.member || 'all',
                    startDate: params?.startDate || '',
                    endDate: params?.endDate || '',
                    status: params?.status || ORDER_STATUS.ALL,
                    classification: params?.classification || null,
                    shipper: params?.shipper || null,
                    consignee: params?.consignee || null,
                },
                appliedSorting: {
                    sortType: params?.sortType || 'desc',
                    sortField: params?.sortField || 'createdAt',
                },
            };
            handleUpdateView(payload);
        } else {
            // Default tab — open create view modal
            handleAction('create_view')();
        }
    };

    const handleFilterView = (isShow: boolean) => () => {
        const currentTabData = tabsData?.find((item) => item.value === activeTab);

        if (isShow) {
            const addParamsValue = {
                appliedFilters: {
                    status: params?.status,
                    classification: params?.classification,
                    search: params?.search,
                    member: params?.member,
                    startDate: params?.startDate,
                    endDate: params?.endDate,
                    shipper: params?.shipper,
                    consignee: params?.consignee,
                },
                appliedSorting: { sortType: params?.sortType, sortField: params?.sortField },
            };
            const tabData = currentTabData?.isIconShow ? currentTabData : { ...currentTabData, ...addParamsValue };
            setIsFilterView({ isShow, data: tabData });
        } else {
            setIsFilterView({ isShow: false, data: null });
            setIsShowAction(false);

            // If params changed from saved view, restore original view filters on cancel
            if (isSaveEnable) {
                if (currentTabData?.isIconShow) {
                    setSearch(currentTabData?.appliedFilters?.search || '');
                    setStartDate(currentTabData?.appliedFilters?.startDate ? moment(currentTabData?.appliedFilters?.startDate).toDate() : null);
                    setEndDate(currentTabData?.appliedFilters?.endDate ? moment(currentTabData?.appliedFilters?.endDate).toDate() : null);

                    setParams((old: any) => ({
                        ...old,
                        search: currentTabData?.appliedFilters?.search,
                        member: currentTabData?.appliedFilters?.member || 'all',
                        startDate: currentTabData?.appliedFilters?.startDate || null,
                        endDate: currentTabData?.appliedFilters?.endDate || null,
                        sortType: currentTabData?.appliedSorting?.sortType,
                        sortField: currentTabData?.appliedSorting?.sortField,
                        status: currentTabData?.appliedFilters?.status,
                        classification: currentTabData?.appliedFilters?.classification,
                        shipper: currentTabData?.appliedFilters?.shipper || null,
                        consignee: currentTabData?.appliedFilters?.consignee || null,
                    }));
                } else {
                    setSearch('');
                    setStartDate(null);
                    setEndDate(null);
                    // Restore the tab that was active when the filter was opened (user may have
                    // changed the Status dropdown mid-session, which changes activeTab)
                    const originalTabValue = isFilterView?.data?.value;

                    if (originalTabValue) {
                        setActiveTab(originalTabValue);
                        dispatch(setActiveTabRedux(originalTabValue));
                    }

                    setParams({ ...initParams, status: originalTabValue || currentTabData?.value });
                }
            }
        }
    };

    const handleRenameView = () => {
        setIsShowAction(false);
        const currentTab = tabsData.find((tab: IOrderTab) => tab.value === activeTab);
        setAction({ ...initAction, rename_view: true, data: { rename: true, tabName: currentTab?.name, id: currentTab?.id } });
    };

    const handleEditView = () => {
        setIsShowAction(false);
        handleFilterView(true)();
    };

    const handleDeleteView = () => {
        setIsShowAction(false);
        const currentTab = tabsData.find((tab: IOrderTab) => tab.value === activeTab);
        setAction({ ...initAction, delete_view: true, data: { view_name: currentTab?.name } });
    };

    const handleDeleteModal = useCallback(
        (status: boolean) => () => {
            if (status) {
                setIsViewChangeLoading(true);
                deleteOrderView(Number(activeTab))
                    .then((response: any) => {
                        if (response?.data) {
                            const newTabs = tabsData.filter((tab: any) => tab.id !== response?.data?.id);
                            setTabsData(newTabs);
                            dispatch(setTabsDataRedux(newTabs));
                            setViewNames((prev: any) => prev.filter((name: any) => name !== response?.data?.name));

                            setActiveTab(ORDER_STATUS.ALL);
                            dispatch(setActiveTabRedux(ORDER_STATUS.ALL));
                            setSearch('');
                            setStartDate(null);
                            setEndDate(null);
                            setParams({ ...initParams });
                            dispatch(setOrderParams({ ...initParams }));
                            handleClose();
                        }
                    })
                    .catch(() => {
                        WalToast.error(t('Common.Something_went_wrong'));
                    })
                    .finally(() => {
                        setIsShowAction(false);
                        setIsViewChangeLoading(false);
                    });
            } else {
                handleClose();
            }
        },
        [activeTab, tabsData]
    );

    const VIEW_ACTIONS = useMemo(
        () => [
            { icon: Edit01, label: t('QuotingHub.QuotingDashboard.Rename_View'), onClick: handleRenameView },
            { icon: CursorBox, label: t('QuotingHub.QuotingDashboard.Edit_View'), onClick: handleEditView },
            { icon: Trash01, label: t('QuotingHub.QuotingDashboard.Delete_View'), onClick: handleDeleteView, isDestructive: true },
        ],
        [activeTab, tabsData]
    );

    const MODEL_TEXT = useMemo(() => {
        const title = action.rename_view ? t('QuotingHub.QuotingDashboard.Rename_View') : t('QuotingHub.QuotingDashboard.Create_New_View');
        const primaryBtnText = action.rename_view ? t('Common.Save') : t('QuotingHub.QuotingDashboard.Create_View');

        return { primaryBtnText, title };
    }, [action]);

    // Column rearrangement handlers
    const handleSortable =
        (isOpen: boolean = true) =>
        () => {
            if (isOpen) {
                const formattedData = formatRowData(ordersList.length ? ordersList : []);

                if (formattedData.length > 0) {
                    const sorted = orderColumns
                        .map((column: any) => {
                            const foundColumn = formattedData[0].find((f: any) => f.id === column.id);

                            return foundColumn ? { ...foundColumn, visible: column.visible } : null;
                        })
                        .filter(Boolean);

                    setIsRowSortable({ isOpen: true, data: [sorted] });
                }
            } else {
                setIsRowSortable(SORTABLE_INITIAL_STATE);
                setColumnsOrder([]);
            }
        };

    const handleColumnChange = (columns: any) => {
        setColumnsOrder(columns);
    };

    const hasColumnChanges = useMemo(() => {
        if (!columnsOrder || columnsOrder.length === 0) return false;

        return columnsOrder.some((col: any) => {
            const originalCol = orderColumns.find((qCol: any) => qCol.id === col.id);

            if (!originalCol) return true;

            return col.displayOrder !== originalCol.displayOrder || col.visible !== originalCol.visible;
        });
    }, [columnsOrder, orderColumns]);

    const handleColumnOrderSubmit = async () => {
        const cleanedColumns = columnsOrder.map((column: any) => {
            // eslint-disable-next-line no-unused-vars
            const { data: data, boxWrapClassName: boxWrapClassName, ...rest } = column;

            return rest;
        });
        dispatch(setOrderColumns(cleanedColumns));
        setIsRowSortable(SORTABLE_INITIAL_STATE);

        updateOrderTableColumnSettings({
            tableAlias: CP_ORDER_TABLE_ALIAS,
            columnSettings: cleanedColumns,
        }).catch(() => {
            // Silent fail — local state already updated
        });
    };

    const handleTabClick = (tab: any) => () => {
        if (tab?.value === activeTab) {
            if (tab?.isIconShow) {
                setIsShowAction((old: any) => !old);
            }

            return;
        }

        setIsShowAction(false);

        if (tab?.appliedSorting) {
            // Custom view tab — restore its saved filters
            setSearch(tab?.appliedFilters?.search || '');
            setStartDate(tab?.appliedFilters?.startDate ? moment(tab?.appliedFilters?.startDate).toDate() : null);
            setEndDate(tab?.appliedFilters?.endDate ? moment(tab?.appliedFilters?.endDate).toDate() : null);

            const updatedParams = {
                ...params,
                page: 1,
                search: tab?.appliedFilters?.search,
                member: tab?.appliedFilters?.member || 'all',
                startDate: tab?.appliedFilters?.startDate || null,
                endDate: tab?.appliedFilters?.endDate || null,
                sortType: tab?.appliedSorting?.sortType,
                sortField: tab?.appliedSorting?.sortField,
                status: tab?.appliedFilters?.status,
                classification: tab?.appliedFilters?.classification,
                shipper: tab?.appliedFilters?.shipper || null,
                consignee: tab?.appliedFilters?.consignee || null,
            };
            setParams(updatedParams);
            dispatch(setOrderParams(updatedParams));
        } else {
            // Default tab — reset to clean state with status filter
            setSearch('');
            setStartDate(null);
            setEndDate(null);

            const updatedParams = { ...initParams, status: tab.value };
            setParams(updatedParams);
            dispatch(setOrderParams(updatedParams));
        }

        setActiveTab(tab?.value);
        dispatch(setActiveTabRedux(tab?.value));
    };

    const handleStatusClassification = (field: string) => (event: any) => {
        const newValue = event?.value ?? (field === 'status' ? ORDER_STATUS.ALL : null);

        setParams((old: any) => ({ ...old, page: 1, [field]: newValue }));

        if (field === 'status' && DEFAULT_ORDER_TAB_VALUES.includes(activeTab)) {
            setActiveTab(newValue);
            dispatch(setActiveTabRedux(newValue));
        }
    };

    const handleShipperConsigneeChange = (field: 'shipper' | 'consignee') => (event: any) => {
        setParams((old: any) => ({ ...old, page: 1, [field]: event?.value ?? null }));
    };

    const handleInputChange = (field: 'shipper' | 'consignee') => (inputValue: string) => {
        if (field === 'shipper') {
            handleSearchShipper(inputValue);
        } else {
            handleSearchConsignee(inputValue);
        }
    };

    return (
        <>
            <PageSectionLayout
                header={<Header title={t('Common.Orders')} Icon={Package} mainContainerClassName="flex-nowrap !flex-row" leftClassName="!w-auto" rightClassName="!w-auto" isShowNotificationBox />}
            >
                <div className="table-box-wrap w-full flex overflow-hidden flex-col">
                    <div className={`transition-all duration-300 ${isFilterView.isShow ? 'h-[90px]' : 'h-[45px]'}`}>
                        {isFilterView.isShow ? (
                            <FilterLayout
                                isSaveBtn={DEFAULT_ORDER_TAB_VALUES?.includes(activeTab) ? isCreateViewEnable : true}
                                onSave={handleViewSave}
                                onCancel={handleFilterView(false)}
                                search={search}
                                handleSearchChange={handleFilter}
                                isSaveEnable={isSaveEnable}
                                isLoading={isViewChangeLoading}
                            >
                                <>
                                    <SelectBox
                                        parentClassName={`filter-view-selectbox filter-view-selectbox-custom ${
                                            params?.status !== ORDER_STATUS.ALL ? 'filter-selectbox-with-value' : 'filter-selectbox-with-no-value'
                                        }`}
                                        SelectDropdownWrapClass="filter-select-dropdown-md"
                                        name="status"
                                        required
                                        className={`form_control`}
                                        options={ORDER_STATUS_OPTIONS}
                                        placeholder={t('Common.Status')}
                                        isClearable={params?.status?.trim()?.toLowerCase() === 'all' ? false : true}
                                        value={params.status ? ORDER_STATUS_OPTIONS?.filter((val: any) => params.status === val?.value) : ORDER_STATUS.ALL}
                                        onChangeFunc={handleStatusClassification('status')}
                                    />
                                    <SelectBox
                                        parentClassName={`filter-view-selectbox filter-view-selectbox-custom ${
                                            params?.classification ? 'filter-selectbox-with-value' : 'filter-selectbox-with-no-value'
                                        }`}
                                        name="classification"
                                        className={`form_control`}
                                        options={ORDER_CLASSIFICATION_OPTIONS}
                                        placeholder={t('Common.Type')}
                                        isClearable={true}
                                        value={params.classification ? ORDER_CLASSIFICATION_OPTIONS?.filter((val: any) => params.classification === val?.value) : null}
                                        onChangeFunc={handleStatusClassification('classification')}
                                    />
                                    {false && (
                                        <SelectBox
                                            parentClassName={`filter-view-selectbox filter-view-selectbox-custom ${
                                                params?.member !== 'all' ? 'w-fit filter-selectbox-with-value' : 'w-[92px] filter-selectbox-with-no-value'
                                            }`}
                                            SelectDropdownWrapClass="filter-select-dropdown-md"
                                            name="userListGroup"
                                            className={`form_control`}
                                            options={teamMemberListOptions}
                                            placeholder={t('Common.User')}
                                            isClearable={true}
                                            isSearchable={true}
                                            value={selectedUserOption}
                                            noOptionMessage={t('QuotingHub.QuotingAnalytics.No_Users_Found')}
                                            onChangeFunc={(event: any, actionMeta: any) => {
                                                setParams((old: any) => ({ ...old, page: 1, member: event?.value ?? 'all' }));

                                                if (actionMeta?.action === 'clear') {
                                                    setSearchTeamMember('');
                                                }
                                            }}
                                            onInputChange={(e: any, { action: actionData }: any) => {
                                                if (actionData === 'input-change' || actionData === 'input-blur') {
                                                    setSearchTeamMember(e);
                                                }
                                            }}
                                        />
                                    )}
                                    <SelectBox
                                        parentClassName={`filter-view-selectbox filter-view-selectbox-custom min-w-[100px] max-w-[160px] ${
                                            params?.shipper ? 'filter-selectbox-with-value' : 'filter-selectbox-with-no-value'
                                        }`}
                                        SelectDropdownWrapClass="filter-select-dropdown-md"
                                        name="shipper"
                                        className={`form_control`}
                                        options={shipperOptions}
                                        placeholder={t('Common.Shipper')}
                                        isClearable={true}
                                        isSearchable={true}
                                        value={params.shipper ? shipperOptions?.filter((val: any) => params.shipper === val?.value) : null}
                                        onChangeFunc={handleShipperConsigneeChange('shipper')}
                                        onInputChange={handleInputChange('shipper')}
                                    />
                                    <SelectBox
                                        parentClassName={`filter-view-selectbox filter-view-selectbox-custom min-w-[100px] max-w-[160px] ${
                                            params?.consignee ? 'filter-selectbox-with-value' : 'filter-selectbox-with-no-value'
                                        }`}
                                        SelectDropdownWrapClass="filter-select-dropdown-md"
                                        name="consignee"
                                        className={`form_control`}
                                        options={consigneeOptions}
                                        placeholder={t('Common.Consignee')}
                                        isClearable={true}
                                        isSearchable={true}
                                        value={params.consignee ? consigneeOptions?.filter((val: any) => params.consignee === val?.value) : null}
                                        onChangeFunc={handleShipperConsigneeChange('consignee')}
                                        onInputChange={handleInputChange('consignee')}
                                    />
                                    <div className="w-fit">
                                        <DateRangePicker
                                            minDate={moment().subtract(1, 'years').startOf('year').toDate()}
                                            maxDate={createDateWithoutTimezoneConversion(moment())}
                                            handleDatePickerChange={(dates: any, type?: string) => handleDatePickerChange(dates, type)}
                                            dateRangeWrapClass={`bg-white !rounded-md !h-[28px] ${params?.startDate && params?.endDate ? '' : '!border-dashed'}`}
                                            selectedTimeRange={selectedTimeRange}
                                            startDate={startDate}
                                            endDate={endDate}
                                            allTimeStartDate={allTimeStartDate}
                                            showYearDropdown={false}
                                            isShowDropDownIcon={false}
                                            isPrevDisabled={isPrevYearDisable}
                                            onPrevNextClick={(type: string) => getFilterRange(type)}
                                            isClearable={true}
                                            containerClassName="xls:left-0 xls:right-[unset] lg:left-[unset] lg:right-0 left-0"
                                            parentClassName=""
                                            textWrapClass="!justify-start flex-row-reverse !gap-x-1.5"
                                            calendarClass="!mr-0"
                                            placeholder={'Date'}
                                            placeholderClassname="!font-semibold !text-textSecondary"
                                        />
                                    </div>
                                </>
                            </FilterLayout>
                        ) : (
                            <div className="table-bottom-header table-header-filter-space !gap-x-2 items-center !border-b border-utilityGray200 !flex-nowrap">
                                <div className={`table-header-bottom-left flex gap-x-1 flex-1 scrollbar-hide ${isRowSortable.isOpen ? 'opacity-60 pointer-events-none !cursor-default' : ''}`}>
                                    <div className="flex items-center gap-x-1">
                                        <TableTabButton
                                            tabArray={tabsData}
                                            tabParentClassName="whitespace-nowrap scrollbar-hide"
                                            isActive={activeTab}
                                            isTabDataInClick={true}
                                            isShowAction={isShowAction}
                                            tabTextClass="!max-w-[104px] truncate"
                                            handleOnClick={handleTabClick}
                                            handleOutSideClick={handleOutSideClick}
                                            isSkeletonLoading={isTabsLoading}
                                            skeletonCount={5}
                                            viewActions={VIEW_ACTIONS}
                                        />
                                        {isCreateViewEnable && !isTabsLoading && (
                                            <div
                                                className="cursor-pointer p-[5px] text-textSecondary text-xs leading-[1.5] font-semibold rounded-lg hover:bg-utilityGray100"
                                                onClick={handleAction('create_view')}
                                            >
                                                <TooltipCmp message={t('QuotingHub.QuotingDashboard.Create_View')}>
                                                    <Plus className="w-4 h-4 flex-none" />
                                                </TooltipCmp>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {!isRowSortable.isOpen ? (
                                    <>
                                        <ButtonCmp className="btn_secondary_black flex-none !px-2 !py-[5px] !gap-1" onClick={handleFilterView(true)}>
                                            <SearchLg className="w-4 h-4" />
                                            <FilterLines className="w-4 h-4" />
                                        </ButtonCmp>
                                        <ButtonCmp
                                            className="btn_secondary_black flex-none"
                                            onClick={handleSortable()}
                                            size="square-sm"
                                            disabled={ordersList.length === 0}
                                            icon={<Columns03 className="w-4 h-4" />}
                                        >
                                            <></>
                                        </ButtonCmp>
                                        <InputText
                                            inputName="searchOrder"
                                            placeholder={t('Common.Search')}
                                            className="bg-white focus:bg-white pl-8 pr-7 placeholder:text-gray500 shadow-sm font-normal search-input"
                                            icon={<SearchLg className="absolute top-1/2 -translate-y-1/2 left-2 text-grayText h-4 w-4" />}
                                            value={search}
                                            inputType="text"
                                            onChangeFunc={handleFilter}
                                            parentClassName="table-searchInput max-mdm:flex-1 max-smd:flex-[0_0_100%] hidden flex-none"
                                            isClearable={true}
                                        />
                                        <ButtonCmp
                                            type="submit"
                                            className={`btn_secondary_black table-filter-btn hidden ${filterCount > 0 ? 'border-borderSecondary hover:border-borderSecondary' : ''}`}
                                            onClick={(e: any) => {
                                                if (e?.target?.id !== 'closeBtn' && e?.target?.parentElement?.id !== 'closeBtn') handleFilterView(true)();
                                            }}
                                            icon={<FilterLines className="w-4 h-4" />}
                                        >
                                            Filters
                                            {filterCount > 0 ? (
                                                <>
                                                    <p className="filter-btn-selected">{filterCount}</p>
                                                    <XClose
                                                        className="w-4 h-4 text-primary700"
                                                        id="closeBtn"
                                                        onClick={(e: any) => {
                                                            e.preventDefault();
                                                            onClearFilter();
                                                        }}
                                                    />
                                                </>
                                            ) : (
                                                ''
                                            )}
                                        </ButtonCmp>
                                        <div className="table-recordsPerPage flex-none">
                                            <SelectBox
                                                SelectDropdownWrapClass="table-recordsPerPageDropdown"
                                                selectLabelWrapClass="table-recordsPerPageSelectValue"
                                                name="recordsPerPageGroup"
                                                id="recordsPerPageGroup"
                                                className="form_control shadow"
                                                size="sm"
                                                options={recordsPerPageArray}
                                                onChangeFunc={(event: any) => {
                                                    setParams((old: any) => {
                                                        if (old.limit === event.value) return old;

                                                        return { ...old, limit: event.value, page: 1 };
                                                    });
                                                }}
                                                isSearchable={false}
                                                value={recordsPerPageArray.find((val: any) => val.value === params.limit)}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <ButtonCmp type="button" onClick={handleSortable(false)} className="btn_secondary_black">
                                            {t('Common.Cancel')}
                                        </ButtonCmp>
                                        <ButtonCmp type="button" onClick={handleColumnOrderSubmit} disabled={!hasColumnChanges} className="btn-outline-primary">
                                            {t('Common.Save')}
                                        </ButtonCmp>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <div className={`draggable-layout-view ${isRowSortable.isOpen ? 'slide-in' : 'slide-out'}`}>
                        <DraggableLayout sortableData={isRowSortable?.data} renderCellContent={OrderDraggableCell} onColumnChange={handleColumnChange} />
                    </div>

                    <div data-test-id="ordersTest" className={`flex-1 w-full table-box-container draggable-layout-view ${isRowSortable.isOpen ? 'slide-out pointer-events-none' : 'slide-in'}`}>
                        {bulkSelection.isOpen && (
                            <BulkActionBar
                                selectedRows={selectedOrderIds}
                                setSelectedRows={setSelectedOrderIds}
                                allIds={ordersList.map((o: any) => o.id)}
                                showOnlySelected={showAllSelected}
                                onShowOnlySelectedChange={setShowAllSelected}
                            />
                        )}
                        <div className="overflow-x-auto custom-scrollbar scrollbar-hide">
                            <TableCmp
                                headCells={headCells}
                                tableDataArr={displayedOrders}
                                TableLoaderRowCmp={NewOrderLoaderRow}
                                TableRowCmp={NewOrderRow}
                                isBulkBarOpen={bulkSelection.isOpen}
                                tableBodyRowClass="bg-white last:border-b-0"
                                tableRowCmpProps={{ onRowClick, navigate, headCells, selectedOrderIds, onToggleSelect: handleToggleSelectOrder }}
                                params={params}
                                setParams={setParams}
                                tableRowHeight="xs"
                                isFirstColumnSticky
                                isActionColumnSticky={hasPriorityOrder || isLoading}
                                isTableDataLoading={isLoading}
                                numberOfSkeletonRows={20}
                                isTableRowClickable={true}
                            />
                        </div>
                        {ordersList.length <= 0 && !isLoading && (
                            <NotFoundUI
                                title={searchApplied ? t('Common.No_Orders_Found') : t('Orders.There_are_no_data_for_orders')}
                                desc={searchApplied ? t('Common.No_Orders_Found_desc') : t('Orders.There_are_no_data_for_orders')}
                                containerClassName="min-h-[calc(100%_-_172.5px)] !h-auto"
                                testId="wal-OrdersDashboard-dataNotFoundText"
                                iconClassName="!hidden"
                                iconSecondaryClassName="!flex"
                                contentClassName="before:!bg-none"
                                iconSecondary={<SearchLg className="w-5 h-5 text-primary" />}
                            />
                        )}
                    </div>

                    {!isRowSortable.isOpen && (
                        <div className="w-full bg-white rounded-b-xl">
                            <CustomPagination recordsPerPage={params.limit} totalRecords={totals} currentPage={params.page} handlePagination={handlePagination} />
                        </div>
                    )}
                </div>
            </PageSectionLayout>

            {(action.create_view || action.rename_view) && (
                <CreateViewModal
                    title={MODEL_TEXT.title}
                    primaryBtnText={MODEL_TEXT.primaryBtnText}
                    handleClose={handleClose}
                    handleSave={handleSave}
                    isLoading={isViewChangeLoading}
                    viewNames={viewNames}
                    modalData={action?.data}
                    isError={isError}
                    setIsError={setIsError}
                />
            )}

            {action.delete_view && (
                <DeleteModal
                    moduleTitle={t('QuotingHub.QuotingDashboard.Delete_View')}
                    moduleDeleteModalContent={t('QuotingHub.QuotingDashboard.Delete_View_desc', {
                        View_name: action?.data?.view_name,
                    })}
                    handleClose={handleDeleteModal}
                    dangerBtnText={t('Common.Delete')}
                    isDeleteLoading={isViewChangeLoading}
                />
            )}
        </>
    );
};

export default Orders;
